/**
 * Tipos de dominio para el módulo de Cierre de Cuentas del Backoffice.
 *
 * @taskId T464, T466
 * @see functional/Specs/specs_cycle_4.md HU-15-FE, HU-16-FE
 */

// ─── Estados de cierre ──────────────────────────────────────────────────────

/**
 * Estado del proceso de cierre.
 * Badge color: CLOSURE_REQUESTED=amarillo, PENDING_FUND_TRANSFER=naranja,
 *              CLOSED=verde, CLOSURE_FAILED=rojo
 */
export type ClosureStatus =
  | 'CLOSURE_REQUESTED'
  | 'PENDING_FUND_TRANSFER'
  | 'FUND_TRANSFERRED'
  | 'CLOSED'
  | 'CLOSURE_FAILED';

/**
 * Tipo de cierre de cuenta.
 */
export type ClosureType = 'VOLUNTARY' | 'INVOLUNTARY';

/**
 * Motivo de cierre involuntario.
 * Requerido por HU-50-BE, seleccionable en el formulario FE.
 */
export type InvoluntaryClosureReason = 'OPERATIVO' | 'REGULATORIO' | 'RIESGO';

// ─── Payloads ───────────────────────────────────────────────────────────────

/**
 * Payload para iniciar cierre involuntario (POST /api/v1/accounts/{id}/close).
 */
export interface CloseAccountInvoluntaryPayload {
  /** Motivo obligatorio (HU-50-BE §reglas de negocio) */
  motivo: InvoluntaryClosureReason;
  /** Descripción adicional opcional */
  observaciones?: string;
}

// ─── Respuestas API ─────────────────────────────────────────────────────────

/**
 * Respuesta del endpoint de cierre involuntario.
 */
export interface CloseAccountResponse {
  /** ID del proceso de cierre iniciado */
  closureId: string;
  /** Estado inicial del proceso */
  status: ClosureStatus;
  /** Mensaje informativo */
  message: string;
}

/**
 * Información de restitución de fondos (si aplica).
 */
export interface RestitutionInfo {
  /** CVU destino de la restitución */
  cuentaDestino: string;
  /** Monto restituido como string (P-02 precisión) */
  monto: string;
  /** Estado de la transferencia */
  estadoTransferencia: 'PENDING' | 'COMPLETED' | 'FAILED';
  /** ISO 8601 fecha de transferencia (si completada) */
  fechaTransferencia?: string;
}

/**
 * Respuesta del endpoint de estado de cierre (HU-51-BE).
 */
export interface ClosureStatusResponse {
  /** ID del proceso de cierre */
  closureId: string;
  /** ID de la cuenta */
  accountId: string;
  /** Estado actual del proceso */
  status: ClosureStatus;
  /** Tipo de cierre */
  type: ClosureType;
  /** Motivo (solo si involuntario) */
  reason?: InvoluntaryClosureReason;
  /** Observaciones adicionales */
  observaciones?: string;
  /** ISO 8601 fecha de solicitud */
  fechaSolicitud: string;
  /** ISO 8601 fecha de cierre efectivo (si CLOSED) */
  fechaCierre?: string;
  /** Información de restitución (si aplica) */
  restitucion?: RestitutionInfo;
  /** Motivo de fallo (si CLOSURE_FAILED) */
  motivoFallo?: string;
}

// ─── Mapping de badges ──────────────────────────────────────────────────────

/**
 * Configuración visual para cada estado de cierre.
 */
export interface ClosureStatusBadgeConfig {
  color: 'yellow' | 'orange' | 'green' | 'red' | 'blue';
  label: string;
}

export const CLOSURE_STATUS_CONFIG: Record<ClosureStatus, ClosureStatusBadgeConfig> = {
  CLOSURE_REQUESTED: { color: 'yellow', label: 'Solicitud de cierre' },
  PENDING_FUND_TRANSFER: { color: 'orange', label: 'Pendiente transferencia' },
  FUND_TRANSFERRED: { color: 'blue', label: 'Fondos transferidos' },
  CLOSED: { color: 'green', label: 'Cerrada' },
  CLOSURE_FAILED: { color: 'red', label: 'Cierre fallido' },
};

/**
 * Labels legibles para motivos de cierre involuntario.
 */
export const INVOLUNTARY_REASON_LABELS: Record<InvoluntaryClosureReason, string> = {
  OPERATIVO: 'Motivo operativo',
  REGULATORIO: 'Motivo regulatorio',
  RIESGO: 'Riesgo detectado',
};
